import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Header from "components/Header/Header.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import image from "../../assets/img/signIn-bg2.webp";
import { makeStyles } from "@mui/styles";
import styles from "../../styles/loginPage";
import { useTranslation } from "react-i18next";
import Availability from "components/Availability";
import HomeFooter from "components/Footer/HomeFooter";
import { api } from "common";
import { FONT_FAMILY } from "common/sharedFunctions";

const useStyles = makeStyles(styles);

const OtpVerificationPage = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const classes = useStyles();
  const { ...rest } = props;

  const signupOtpState = useSelector((state) => state.signupotpdata);
  const { verifySignupOtp, requestSignupOtp, clearSignupOtpState } = api;

  // Get data from navigation state
  const { userData, deliveryMethod, contact } = location.state || {};

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [countdown, setCountdown] = useState(60);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  // Redirect if no user data
  useEffect(() => {
    if (!userData || !deliveryMethod) {
      navigate("/login");
    }
  }, [userData, deliveryMethod, navigate]);

  // Focus first input on mount
  useEffect(() => {
    setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 100);
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }

    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (value && index === 5) {
      const fullOtp = newOtp.join("");
      if (fullOtp.length === 6) {
        handleVerify(fullOtp);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (pastedData.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);

      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();

      if (pastedData.length === 6) {
        handleVerify(pastedData);
      }
    }
  };

  const handleVerify = async (otpValue) => {
    const fullOtp = otpValue || otp.join("");
    if (fullOtp.length !== 6) return;

    setError("");
    setLoading(true);

    try {
      const result = await dispatch(
        verifySignupOtp(
          userData.email,
          userData.mobile,
          fullOtp,
          deliveryMethod,
        ),
      );

      if (result.success) {
        setSuccess(true);
        dispatch(clearSignupOtpState());

        // Navigate to sign in page
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      } else {
        setError(
          result.error ||
            t("otp_verification_failed") ||
            "Invalid OTP. Please try again.",
        );
        setLoading(false);
      }
    } catch (err) {
      setError(
        t("otp_verification_failed") ||
          "Verification failed. Please try again.",
      );
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setResendLoading(true);
    setCountdown(60);
    setOtp(["", "", "", "", "", ""]);

    try {
      const result = await dispatch(requestSignupOtp(userData, deliveryMethod));
      if (!result.success) {
        setError(
          result.error || t("otp_resend_failed") || "Failed to resend OTP",
        );
      }
    } catch (err) {
      setError(t("otp_resend_failed") || "Failed to resend OTP");
    }

    setResendLoading(false);
    inputRefs.current[0]?.focus();
  };

  const handleBack = () => {
    dispatch(clearSignupOtpState());
    navigate(-1);
  };

  const isComplete = otp.every((digit) => digit !== "");

  if (!userData || !deliveryMethod) {
    return null;
  }

  return (
    <div>
      <div
        style={{
          backgroundImage: "url(" + image + ")",
          backgroundSize: "cover",
          backgroundPosition: "top center",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Header
          absolute
          color="transparent"
          rightLinks={<HeaderLinks />}
          {...rest}
        />
        <div className={classes.pageHeader}>
          <Box
            sx={{
              width: "100%",
              maxWidth: 500,
              marginTop: "40px",
              zIndex: 2,
              p: 4,
              borderRadius: 4,
            }}
          >
            {/* Back Button */}
            <Button
              onClick={handleBack}
              disabled={loading || success}
              startIcon={<ArrowBackIcon />}
              sx={{
                color: "#fff",
                mb: 10,
                textTransform: "none",
                fontFamily: FONT_FAMILY,
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              {t("go_back") || "Go Back"}
            </Button>

            {/* Header with Icon */}
            <Box
              sx={{
                textAlign: "center",
                mb: 4,
              }}
            >
              {/* <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "rgba(89, 169, 89, 0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                  border: "2px solid rgba(89, 169, 89, 0.5)",
                }}
              >
                {deliveryMethod === "email" ? (
                  <EmailOutlinedIcon sx={{ fontSize: 40, color: "#fff" }} />
                ) : (
                  <SmsOutlinedIcon sx={{ fontSize: 40, color: "#fff" }} />
                )}
              </Box> */}

              <Typography
                variant="h4"
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                {t("verify_your_account") || "Verify Your Account"}
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  fontFamily: FONT_FAMILY,
                  fontSize: "16px",
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                {deliveryMethod === "whatsapp" ? (
                  <>
                    <WhatsAppIcon sx={{ color: "#25D366", fontSize: 20 }} />
                    {t("otp_sent_to_whatsapp") || "We sent a 6-digit code to your WhatsApp"}
                  </>
                ) : (
                  <>
                    <SmsOutlinedIcon sx={{ color: "#AFCD56", fontSize: 20 }} />
                    {t("otp_sent_to_sms") || "We sent a 6-digit code via SMS to"}
                  </>
                )}
              </Typography>

              <Typography
                sx={{
                  color: "#AFCD56",
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                  fontSize: "18px",
                  wordBreak: "break-all",
                }}
              >
                {contact}
              </Typography>
            </Box>

            {/* Error/Success Messages */}
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  fontFamily: FONT_FAMILY,
                  borderRadius: "12px",
                }}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  fontFamily: FONT_FAMILY,
                  borderRadius: "12px",
                }}
              >
                {t("account_create_successfully") ||
                  "Account created successfully!"}
              </Alert>
            )}

            {/* OTP Input Boxes */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: { xs: 1, sm: 1.5 },
                mb: 3,
              }}
            >
              {otp.map((digit, index) => (
                <Box
                  key={index}
                  component="input"
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  disabled={loading || success}
                  sx={{
                    width: { xs: 50, sm: 60 },
                    height: { xs: 60, sm: 72 },
                    border: digit
                      ? "2px solid #AFCD56"
                      : "2px solid rgba(255,255,255,0.5)",
                    borderRadius: "12px",
                    textAlign: "center",
                    fontSize: { xs: "28px", sm: "32px" },
                    fontWeight: 700,
                    fontFamily: FONT_FAMILY,
                    color: "#fff",
                    outline: "none",
                    transition: "all 0.2s ease",
                    backgroundColor: digit
                      ? "rgba(89, 169, 89, 0.2)"
                      : "rgba(255,255,255,0.1)",
                    "&:focus": {
                      borderColor: "#AFCD56",
                      boxShadow: "0 0 0 3px rgba(175, 205, 86, 0.3)",
                    },
                    "&:disabled": {
                      backgroundColor: "rgba(255,255,255,0.05)",
                      cursor: "not-allowed",
                    },
                  }}
                />
              ))}
            </Box>

            {/* Timer / Resend */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              {countdown > 0 ? (
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    fontFamily: FONT_FAMILY,
                    fontSize: "15px",
                  }}
                >
                  {t("resend_code_in") || "Resend code in"}{" "}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 600,
                      color: "#AFCD56",
                      fontFamily: "monospace",
                      fontSize: "16px",
                    }}
                  >
                    {Math.floor(countdown / 60)}:
                    {String(countdown % 60).padStart(2, "0")}
                  </Box>
                </Typography>
              ) : (
                <Button
                  onClick={handleResend}
                  disabled={loading || resendLoading || success}
                  sx={{
                    textTransform: "none",
                    color: "#AFCD56",
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                    fontSize: "16px",
                    "&:hover": {
                      backgroundColor: "rgba(175, 205, 86, 0.15)",
                    },
                  }}
                >
                  {resendLoading ? (
                    <CircularProgress
                      size={20}
                      sx={{ color: "#AFCD56", mr: 1 }}
                    />
                  ) : null}
                  {t("resend_code") || "Resend Code"}
                </Button>
              )}
            </Box>

            {/* Verify Button */}
            <Button
              fullWidth
              onClick={() => handleVerify()}
              disabled={!isComplete || loading || success}
              sx={{
                py: 1.75,
                borderRadius: "50px",
                textTransform: "none",
                fontSize: "18px",
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
                background:
                  isComplete && !success
                    ? "linear-gradient(90deg, #59A959 0%, #79B357 50%, #AFCD56 100%)"
                    : "rgba(255,255,255,0.3)",
                color: isComplete ? "white" : "rgba(255,255,255,0.6)",
                boxShadow:
                  isComplete && !success
                    ? "0 4px 15px rgba(89, 169, 89, 0.4)"
                    : "none",
                transition: "all 0.3s ease",
                "&:hover": {
                  background:
                    isComplete && !success
                      ? "linear-gradient(90deg, #4a9a4a 0%, #6aa348 50%, #9fbd46 100%)"
                      : "rgba(255,255,255,0.3)",
                  boxShadow:
                    isComplete && !success
                      ? "0 6px 20px rgba(89, 169, 89, 0.5)"
                      : "none",
                },
                "&:disabled": {
                  background: success
                    ? "linear-gradient(90deg, #59A959 0%, #79B357 50%, #AFCD56 100%)"
                    : "rgba(255,255,255,0.2)",
                  color: success ? "white" : "rgba(255,255,255,0.5)",
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <CircularProgress size={24} sx={{ color: "white" }} />
                  <span>{t("verifying") || "Verifying..."}</span>
                </Box>
              ) : success ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 24 }} />
                  <span>{t("success") || "Success!"}</span>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {isComplete && <CheckCircleIcon sx={{ fontSize: 22 }} />}
                  <span>{t("verify_and_continue") || "Verify & Continue"}</span>
                </Box>
              )}
            </Button>

            {/* Help text */}
            <Typography
              sx={{
                textAlign: "center",
                color: "rgba(255,255,255,0.6)",
                fontSize: "13px",
                fontFamily: FONT_FAMILY,
                mt: 3,
              }}
            >
              {deliveryMethod === "whatsapp"
                ? t("otp_help_text_whatsapp") || "Didn't receive the code? Check your WhatsApp messages"
                : t("otp_help_text_sms") || "Didn't receive the code? Check your SMS inbox"}
            </Typography>
          </Box>
        </div>
      </div>

      <Availability />
      <HomeFooter />
    </div>
  );
};

export default OtpVerificationPage;
